import React, { useEffect, useRef, useState } from "react";
import { Microphone, Stop, Sparkle, Warning } from "@phosphor-icons/react";
import api from "../lib/api";
import { toast } from "sonner";

const MAX_SECONDS = 30;

/**
 * VoiceRecorder — botón de nota de voz para pre-llenar el reporte.
 * Al terminar, envía el audio a /ai/voice-parse y devuelve el JSON parseado a `onResult`.
 *
 * Props:
 *   onResult({ transcript, severity, note, candidates })   // llamado en éxito
 *   disabled?: bool
 */
export default function VoiceRecorder({ onResult, disabled }) {
  const [supported, setSupported] = useState(true);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [levels, setLevels] = useState([0, 0, 0, 0, 0, 0, 0, 0]);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setSupported(false);
    }
    return () => cleanup();
    // Ejecuta cleanup solo al desmontar; cleanup lee refs, no captura state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanup = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (_) {}
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
  };

  const start = async () => {
    if (disabled || uploading) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        const ctx = new AC();
        audioCtxRef.current = ctx;
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 64;
        analyserRef.current = analyser;
        ctx.createMediaStreamSource(stream).connect(analyser);
        const buf = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          analyser.getByteFrequencyData(buf);
          const step = Math.floor(buf.length / 8);
          const arr = Array.from({ length: 8 }, (_, i) => {
            const start = i * step;
            const slice = buf.slice(start, start + step);
            const avg = slice.reduce((a, b) => a + b, 0) / (slice.length || 1);
            return Math.min(1, avg / 200);
          });
          setLevels(arr);
          rafRef.current = requestAnimationFrame(tick);
        };
        tick();
      }
      let mimeType = "";
      const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
      for (const c of candidates) {
        if (window.MediaRecorder && MediaRecorder.isTypeSupported(c)) { mimeType = c; break; }
      }
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = handleStop;
      rec.start();
      setSeconds(0);
      setRecording(true);
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          const nx = s + 1;
          if (nx >= MAX_SECONDS) stop();
          return nx;
        });
      }, 1000);
    } catch (e) {
      setSupported(false);
      toast.error("No se pudo acceder al micrófono. Revisa permisos del navegador.");
    }
  };

  const stop = () => {
    if (!recording) return;
    setRecording(false);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    try { mediaRef.current?.stop(); } catch (_) {}
  };

  const handleStop = async () => {
    const mime = mediaRef.current?.mimeType || "audio/webm";
    const blob = new Blob(chunksRef.current, { type: mime });
    cleanup();
    if (blob.size < 1000) {
      toast.error("La grabación fue demasiado corta. Intenta de nuevo.");
      setLevels([0, 0, 0, 0, 0, 0, 0, 0]);
      return;
    }
    const ext = mime.includes("mp4") ? "mp4" : mime.includes("ogg") ? "ogg" : "webm";
    const fd = new FormData();
    fd.append("file", blob, `nota.${ext}`);
    setUploading(true);
    try {
      const { data } = await api.post("/ai/voice-parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      onResult?.(data);
      if (!data.transcript) toast.warning("No se pudo entender el audio. Intenta hablar más claro.");
      else toast.success("Nota transcrita · revisa los datos");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo procesar la nota. Reintenta.");
    } finally {
      setUploading(false);
      setLevels([0, 0, 0, 0, 0, 0, 0, 0]);
    }
  };

  if (!supported) {
    return (
      <div className="rounded-lg border border-dashed border-[color:var(--q-border)] bg-neutral-50 p-3 text-xs text-[color:var(--q-muted)] flex items-center gap-2" data-testid="voice-unsupported">
        <Warning size={14}/> Tu navegador no soporta grabación de voz. Usa el formulario manual.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-[color:var(--q-navy)]/30 bg-[color:var(--q-navy)]/5 p-3" data-testid="voice-recorder">
      <div className="flex items-center gap-3">
        {!recording ? (
          <button
            type="button"
            onClick={start}
            disabled={disabled || uploading}
            data-testid="voice-start-btn"
            className="h-11 px-4 rounded-lg bg-[color:var(--q-navy)] text-white font-semibold flex items-center gap-2 hover:opacity-90 disabled:opacity-50 transition"
          >
            <Microphone size={18} weight="fill"/>
            {uploading ? "Procesando…" : "Grabar nota de voz"}
          </button>
        ) : (
          <button
            type="button"
            onClick={stop}
            data-testid="voice-stop-btn"
            className="h-11 px-4 rounded-lg bg-rose-600 text-white font-semibold flex items-center gap-2 animate-pulse"
          >
            <Stop size={18} weight="fill"/>
            Detener ({MAX_SECONDS - seconds}s)
          </button>
        )}
        <div className="flex-1 flex items-end gap-1 h-10">
          {levels.map((v, i) => (
            <div key={i}
                 className="flex-1 rounded-sm transition-all"
                 style={{
                   height: `${Math.max(4, v * 100)}%`,
                   backgroundColor: recording ? "#C9A227" : "#E5E7EB",
                 }}/>
          ))}
        </div>
        {uploading && <Sparkle size={20} className="text-[color:var(--q-gold)] animate-pulse" weight="fill"/>}
      </div>
      <div className="text-[11px] text-[color:var(--q-muted)] mt-2 flex items-center gap-1">
        <Sparkle size={11} weight="fill" className="text-[color:var(--q-gold)]"/>
        Hasta 30s · La IA transcribirá y pre-llenará el formulario. Revisa antes de enviar.
      </div>
    </div>
  );
}
